<template>
  <div>
    <b-card class="card-custom" header-tag="header" footer-tag="footer">
      <template v-slot:header>
        <div class="card-title">
          <h3 class="card-label">
            {{ pageTitle }}
            <i class="mr-2"></i>
            <small class="">{{ $t("LIST") }}</small>
          </h3>
        </div>
        <div class="card-toolbar"></div>
      </template>
      <v-card v-show="!isPageLoading">
        <SearchTools
          :searchQuery.sync="searchQuery"
          @do-search="doSearch"
          @do-clear="doClear"
        >
          <template v-slot:prepend>
            <b-col :sm="6">
              <FormDatepickerBase
                :type="'text'"
                :clearable="true"
                :max="maxDate"
                :i18n="{
                  label: 'FROM',
                  placeholder: $t('FROM')
                }"
                :date.sync="filters.fromDate"
              ></FormDatepickerBase>
            </b-col>

            <b-col :sm="6">
              <FormDatepickerBase
                :type="'text'"
                :clearable="true"
                :max="maxDate"
                :i18n="{
                  label: 'TO',
                  placeholder: $t('TO')
                }"
                :date.sync="filters.toDate"
              ></FormDatepickerBase>
            </b-col>
            <b-col
              :lg="3"
              :md="12"
              v-if="!isPageLoading && _.size(fullStores) > 0"
            >
              <v-autocomplete
                :items="fullStores"
                :label="$t('SENDER')"
                item-text="name"
                item-value="id"
                hide-details
                clearable
                v-model="filters.storeId"
                @click:clear="clearFilter('storeId')"
              ></v-autocomplete>
            </b-col>
            <b-col :lg="3" :md="12" v-if="!isPageLoading && _.size(stores) > 0">
              <v-autocomplete
                :items="stores"
                :label="$t('RECEIVER')"
                item-text="name"
                item-value="id"
                hide-details
                clearable
                v-model="filters.fromStoreId"
                @click:clear="clearFilter('fromStoreId')"
              ></v-autocomplete>
            </b-col>
          </template>
        </SearchTools>
        <v-data-table
          :headers="tableHeaders"
          :items="items"
          :options.sync="options"
          :items-per-page="totalItems"
          :server-items-length="totalItems"
          sort-by="createdAt"
          sort-desc="true"
          :loading="isLoadingStock"
          loading-text="Loading... Please wait"
          :footer-props="{
            showFirstLastPage: true,
            itemsPerPageOptions: rowsPerPageItems,
            firstIcon: 'mdi-arrow-collapse-left',
            lastIcon: 'mdi-arrow-collapse-right',
            itemsPerPageText: $t('ITEMS_PER_PAGE_TEXT')
          }"
          @update:options="getDataFromApi()"
          class="elevation-1"
        >
          <template v-slot:item.createdAt="{ item }">
            <router-link
              v-if="checkIfUserHasPermission(['stock-purchase-orders-show'])"
              :to="{
                name: 'stock-purchase-order-discrepancy',
                params: { id: item.id }
              }"
            >
              {{ item.createdAt | formatDateTime }}</router-link
            >
            <template v-else>{{ item.createdAt | formatDateTime }}</template>
          </template>

          <template v-slot:item.actions="{ item }">
            <div class="d-flex justify-content-end">
              <template>
                <ActionButton
                  :to="{
                    name: 'stock-purchase-order-discrepancy',
                    params: { id: item.id }
                  }"
                  :permissions="['stock-purchase-orders-update']"
                  :label="$t('SOLVE_DISCREPANCY')"
                  :icon="'mdi-reload'"
                />
              </template>
            </div>
          </template>
          <template v-slot:no-results>
            <v-alert :value="true" color="error" icon="warning">
              Your search for "{{ searchQuery }}" found no results.
            </v-alert>
          </template>
        </v-data-table>
      </v-card>

      <v-skeleton-loader
        v-if="isPageLoading"
        :loading="isPageLoading"
        type="table"
      ></v-skeleton-loader>
      <v-overlay :value="!firstLoader && isLoadingSharedStore">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <template v-slot:footer></template>
    </b-card>
  </div>
</template>
<style scoped>
tr.previewTr th {
  font-size: 18px !important;
}
</style>
<script>
//General

import { mapGetters } from "vuex";
import SearchTools from "@/core/components/table/SearchTools.vue";
import ActionButton from "@/core/components/table/ActionButton.vue";
import FormDatepickerBase from "@/view/content/forms/components/FormDatepickerBase.vue";

//Audit Module
import { FETCH_STOCK_PURCHASE_ORDERS_DISCREPANCY } from "@/modules/stock/store/stock.module";

import { FETCH_STORES_BASIC } from "@/core/services/store/shared_store.module";

//Shared Modules
import { FETCH_SELECTS_BASIC } from "@/core/services/store/shared_base.module";

import { permissionMixin } from "@/core/mixins/permissionMixin.js";

import _ from "lodash";
import moment from "moment";
export default {
  name: "StockPurchaseOrderListDiscrepancy",
  mixins: [permissionMixin],
  props: {
    statusId: {
      default: null
    },
    pageTitle: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      firstLoader: true,
      isPageLoading: true,
      items: [],
      stores: [],
      fullStores: [],
      totalItems: 100,
      searchQuery: "",
      filters: {
        fromStoreId: null,
        storeId: null,
        fromDate: null,
        toDate: null
      },
      options: {
        descending: true,
        page: 1,
        itemsPerPage: 10,
        totalItems: 100
      },
      stockPurchaseOrderStatuses: [],
      rowsPerPageItems: [10, 20, 30, 40, 50],
      columns: {},
      cancelSource: null,
      selectedItemId: null,
      headers: [
        {
          text: this.$t("ID"),
          value: "id"
        },
        {
          text: this.$t("FROM_DATE"),
          value: "createdAt"
        },
        {
          text: this.$t("SENDER"),
          value: "toStoreName",
          sortable: false
        },
        {
          text: this.$t("RECEIVER"),
          value: "fromStoreName",
          sortable: false
        },
        {
          text: this.$t("USER_REQUESTED"),
          value: "userName",
          sortable: false
        },
        {
          align: "right",
          text: this.$t("ACTIONS"),
          value: "actions",
          sortable: false,
          width: "10%"
        }
      ]
    };
  },
  components: {
    SearchTools,
    ActionButton,
    FormDatepickerBase
  },
  watch: {
    storesParams(value) {
      let vm = this;
      vm.filters.storeId = null;
      vm.getStores();
    },
    fullStoresParams(value) {
      let vm = this;
      vm.filters.fromStoreId = null;
      vm.getFullStores();
    }
  },
  mounted() {
    let vm = this;
    vm.getStores();
    vm.getFullStores();
  },
  computed: {
    ...mapGetters([
      "getItems",
      "isLoadingStock",
      "isLoadingSharedStore",
      "currentUser"
    ]),
    params() {
      return {
        ...this.options,
        query: this.searchQuery,
        fromStoreId: this.filters.fromStoreId,
        toStoreId: this.filters.storeId,
        fromDate: this.filters.fromDate,
        toDate: this.filters.toDate
      };
    },
    storesParams() {
      let storesParams = {};
      return storesParams;
    },
    fullStoresParams() {
      let fullStoresParams = { full: 1 };
      return fullStoresParams;
    },
    tableHeaders() {
      let vm = this;

      return vm.headers;
    },
    maxDate() {
      return moment().format("YYYY-MM-DD");
    }
  },
  methods: {
    clearFilter(key) {
      let vm = this;
      vm.$nextTick(function() {
        this.$set(vm.filters, key, null);
      });
    },
    cancelRequest() {
      //Axios cancelSource to stop current search if new value is entered
      if (this.cancelSource) {
        this.cancelSource.cancel("Start new search, stop active search");
      }
    },
    doSearch() {
      let vm = this;
      if (vm.options.page == 1) {
        vm.getDataFromApi();
      } else {
        vm.options.page = 1;
      }
    },
    doClear() {
      let vm = this;
      vm.searchQuery = "";
      vm.filters.storeId = null;
      if (vm.options.page == 1) {
        vm.getDataFromApi();
      } else {
        vm.options.page = 1;
      }
    },
    getDataFromApi() {
      let vm = this;
      //copy current params to modify
      let params = this.params;

      params.length = params.itemsPerPage; //set how many records to fecth per page
      params.start =
        params.page == 1 ? 0 : params.itemsPerPage * (params.page - 1); //set offset
      let apiParams = vm.transformUrlParams(params);

      vm.$store
        .dispatch(FETCH_STOCK_PURCHASE_ORDERS_DISCREPANCY, apiParams)
        .then(data => {
          // vm.$notify({
          //   group: "notify",
          //   type: "success",
          //   title: "<i class='flaticon2-checkmark'></i> Success",
          //   text: data.message
          // });
          vm.firstLoader = false;
          vm.$nextTick(function() {
            vm.items = data.data.items;
            vm.totalItems = data.data.totalItemsCount;

            if (vm.isPageLoading == true) {
              vm.isPageLoading = false;
            }
          });
        })
        .catch(response => {
          console.log(response);
          if (response.status === 404) {
            vm.$notify({
              group: "notify",
              type: "error",
              text: "Nout Found"
            });
            this.$router.push({ name: "stock-purchase-orders-list" });
          }
        });
    },
    getStores() {
      let vm = this;
      let params = this.storesParams;
      let apiParams = vm.transformUrlParams(params);
      this.$store
        .dispatch(FETCH_STORES_BASIC, apiParams)
        .then(data => {
          this.$set(vm, "stores", data.data);
        })
        .catch(response => {
          console.log(response);
        });
    },
    getFullStores() {
      let vm = this;
      let params = this.fullStoresParams;
      let apiParams = vm.transformUrlParams(params);
      this.$store
        .dispatch(FETCH_STORES_BASIC, apiParams)
        .then(data => {
          this.$set(vm, "fullStores", data.data);
        })
        .catch(response => {
          console.log(response);
        });
    },
    getSelects() {
      let vm = this;
      let payload = {
        stock_purchase_order_statuses: {
          fields: ["id", "name", "description"],
          search: ""
        }
      };
      this.$store
        .dispatch(FETCH_SELECTS_BASIC, payload)
        .then(data => {
          vm.stockPurchaseOrderStatuses =
            data.data.stock_purchase_order_statuses;
        })
        .catch(response => {
          console.log(response);
        });
    },
    transformUrlParams(params) {
      let apiParams =
        "?" +
        Object.keys(params)
          .map(key => key + "=" + params[key])
          .join("&");
      return apiParams;
    }
  }
};
</script>
